import { useState, useEffect } from 'react';
import { adminApi } from '../../../services/admin-api';

interface Broadcast {
  id: string;
  senderDisplayName: string | null;
  title: string;
  content: string;
  broadcastType: string;
  priority: number;
  isPublished: boolean;
  publishedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const typeColors: Record<string, string> = {
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  promo: 'bg-green-100 text-green-800',
  fun: 'bg-purple-100 text-purple-800',
};

export function BroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    broadcastType: 'info',
    priority: 0,
    expiresAt: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchBroadcasts = async () => {
    try {
      const data = await adminApi.listBroadcasts();
      setBroadcasts(data.broadcasts);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const resetForm = () => {
    setFormData({ title: '', content: '', broadcastType: 'info', priority: 0, expiresAt: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (broadcast: Broadcast) => {
    setFormData({
      title: broadcast.title,
      content: broadcast.content,
      broadcastType: broadcast.broadcastType,
      priority: broadcast.priority,
      expiresAt: broadcast.expiresAt ? broadcast.expiresAt.slice(0, 16) : '',
    });
    setEditingId(broadcast.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        broadcastType: formData.broadcastType,
        priority: formData.priority,
        expiresAt: formData.expiresAt || null,
      };

      if (editingId) {
        await adminApi.updateBroadcast(editingId, payload);
      } else {
        await adminApi.createBroadcast(payload);
      }
      resetForm();
      await fetchBroadcasts();
    } catch (err) {
      console.error('Failed to save broadcast:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id: string, publish: boolean) => {
    try {
      await adminApi.publishBroadcast(id, publish);
      await fetchBroadcasts();
    } catch (err) {
      console.error('Failed to publish broadcast:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this broadcast?')) return;
    try {
      await adminApi.deleteBroadcast(id);
      await fetchBroadcasts();
    } catch (err) {
      console.error('Failed to delete broadcast:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Broadcasts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Send announcements to all users. Published broadcasts appear as banners in the app.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          New Broadcast
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Broadcast' : 'New Broadcast'}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Short headline..."
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Message body..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.broadcastType}
                  onChange={(e) => setFormData({ ...formData, broadcastType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="info">Info (blue)</option>
                  <option value="warning">Warning (amber)</option>
                  <option value="promo">Promo (green)</option>
                  <option value="fun">Fun (purple)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving || !formData.title.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create Draft'}
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcasts List */}
      {broadcasts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No broadcasts yet. Create your first one!
        </div>
      ) : (
        <div className="space-y-3">
          {broadcasts.map((broadcast) => (
            <div
              key={broadcast.id}
              className={`bg-white border rounded-lg p-4 shadow-sm ${
                broadcast.isPublished ? 'border-green-200' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{broadcast.title}</h3>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        typeColors[broadcast.broadcastType] || typeColors.info
                      }`}
                    >
                      {broadcast.broadcastType}
                    </span>
                    {broadcast.isPublished ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Live
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        Draft
                      </span>
                    )}
                    {broadcast.priority > 0 && (
                      <span className="text-xs text-gray-400">priority: {broadcast.priority}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{broadcast.content}</p>
                  <div className="text-xs text-gray-400 space-x-3">
                    <span>Created: {new Date(broadcast.createdAt).toLocaleDateString()}</span>
                    {broadcast.publishedAt && (
                      <span>Published: {new Date(broadcast.publishedAt).toLocaleDateString()}</span>
                    )}
                    {broadcast.expiresAt && (
                      <span>Expires: {new Date(broadcast.expiresAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {broadcast.isPublished ? (
                    <button
                      onClick={() => handlePublish(broadcast.id, false)}
                      className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100"
                    >
                      Unpublish
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePublish(broadcast.id, true)}
                      className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                    >
                      Publish
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(broadcast)}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(broadcast.id)}
                    className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
